import BottomSheetAlert from '@/components/ui/bottomsheet/BottomSheetAlert.vue'
import type { BottomSheetConfig } from '@/components/ui/bottomsheet/typedefs'
import { defineStore } from 'pinia'
import { computed, h, ref } from 'vue'

interface AlertConfig {
  text: string
  buttonText?: string
  onClose?: () => void
}

/**
 * The following store tracks global bottom sheet instance.
 *
 * The instance is rendered within the <UiBottomSheet/> component
 * which is rendered within App.vue.
 */
export const useUiBottomSheetStore = defineStore('uiBottomSheet', () => {
  const openedBottomSheetConfig = ref<BottomSheetConfig | null>(null)

  const open = (config?: BottomSheetConfig) => {
    openedBottomSheetConfig.value = {
      ...config
    }
  }

  const close = () => {
    openedBottomSheetConfig.value?.onClose?.()
    openedBottomSheetConfig.value = null
  }

  const openAlert = (alertConfig: AlertConfig) => {
    openedBottomSheetConfig.value = {
      content: () =>
        h(BottomSheetAlert, {
          text: alertConfig.text,
          buttonText: alertConfig.buttonText ?? 'OK',
          onClose: () => {
            close()
            alertConfig?.onClose?.()
          }
        })
    }
  }

  const $reset = () => {
    openedBottomSheetConfig.value = null
  }

  return {
    // Generic API.
    open,
    close,

    // Shortcut to open simple bottom sheet.
    openAlert,

    // Public state.
    isOpen: computed(() => openedBottomSheetConfig.value !== null),
    openedConfig: computed(() => openedBottomSheetConfig.value),

    $reset
  }
})
